/**
 * Single source of truth for every economic value used by the estimator.
 * No component, view or test should hardcode a price — everything must
 * read from this module. Bump `version` whenever a tier or rate changes;
 * it travels with every quote so mismatches are traceable later.
 */

export type ModalityKey =
  | "whiteBackground"
  | "flatLay"
  | "invisibleMannequin"
  | "ambient"
  | "model"
  | "onLocation";

export interface WhiteBackgroundTier {
  /** Inclusive lower bound of total images for this tier. */
  min: number;
  /** Inclusive upper bound, or null for "and above". */
  max: number | null;
  /** MXN price per image within this tier. */
  perImage: number;
}

export interface AutomaticPricingModality {
  enabled: true;
  automaticPricing: true;
  fromPerImage: number;
  requiresProductionValidation?: boolean;
}

export interface WhiteBackgroundModality {
  enabled: true;
  automaticPricing: true;
  tiers: WhiteBackgroundTier[];
}

export interface ManualPricingModality {
  enabled: true;
  automaticPricing: false;
}

export const pricing = {
  version: "2026-08-preview",
  currency: "MXN",

  whiteBackground: {
    enabled: true,
    automaticPricing: true,
    // Demo scale carried over from the landing page spec. NOT approved for
    // public/definitive display — this app only ever labels it "referencia".
    tiers: [
      { min: 1, max: 59, perImage: 200 },
      { min: 60, max: 99, perImage: 180 },
      { min: 100, max: 199, perImage: 160 },
      { min: 200, max: null, perImage: 150 }
    ]
  } satisfies WhiteBackgroundModality,

  flatLay: {
    enabled: true,
    automaticPricing: true,
    fromPerImage: 200
  } satisfies AutomaticPricingModality,

  invisibleMannequin: {
    enabled: true,
    automaticPricing: true,
    fromPerImage: 600
  } satisfies AutomaticPricingModality,

  ambient: {
    enabled: true,
    automaticPricing: true,
    fromPerImage: 600,
    requiresProductionValidation: true
  } satisfies AutomaticPricingModality,

  model: {
    enabled: true,
    automaticPricing: false
  } satisfies ManualPricingModality,

  onLocation: {
    enabled: true,
    automaticPricing: false
  } satisfies ManualPricingModality
} as const;

export type Pricing = typeof pricing;

export const PRODUCTS_MIN = 1;
export const PRODUCTS_MAX = 999;
export const PRODUCTS_DEFAULT = 15;

export const PHOTOS_PER_PRODUCT_MIN = 1;
export const PHOTOS_PER_PRODUCT_MAX = 20;
export const PHOTOS_PER_PRODUCT_DEFAULT = 5;

export const WHATSAPP_NUMBER =
  (import.meta as any).env?.VITE_WHATSAPP_NUMBER || "5215547405899";
